/**
 * Outfits and mascot › Economy (GET /v1/admin/desktop/mascot/economy, PUT …/economy/items/:id, …/collections/:id,
 * …/packs/:id, GET …/economy/supply; docs/STRATEGY-outfit-economy.md section 5.11; owner, 2026-10-15): per outfit its
 * kind, rarity (a kyber crystal), supply (unlimited, or a cap with the copies given out and left), status, whether it
 * can be traded, its collection, the animations it still needs for its rarity, and the store price its band proposes;
 * the collections and packs; the supply report (every capped outfit with copies given out, left, sold-out date and
 * holders); the history. Hidden on a service with the economy off (404). The rules and words are in lib/outfitEconomy.ts.
 */
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Gem, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import type { DesktopMascotCatalog } from '../lib/desktopMascot';
import {
  ANIMATIONS_MINIMUM,
  COLLECTION_ID_RE,
  ECONOMY_HINT,
  ECONOMY_NAME_MAX,
  ECONOMY_SAVED_TEXT,
  ECONOMY_UNREADABLE_TEXT,
  ITEM_STATUSES,
  KEEP_ITEM_ID_RE,
  KYBER_CRYSTAL_PATH,
  PACK_ID_RE,
  PACK_ITEMS_MAX,
  RARITIES,
  RARITY_LABELS,
  animationsGapText,
  bandHintText,
  crystalOf,
  economyChanged,
  economyRefusalText,
  historyLineText,
  itemBody,
  itemFormOf,
  itemFormRefusal,
  rarityOptionText,
  supplyOf,
  supplyStatusText,
  supplyText,
  withRarity,
  type EconomyView,
  type ItemForm,
  type ItemStatus,
  type Rarity,
  type SupplyRow,
} from '../lib/outfitEconomy';
import { Button, Callout, Checkbox, Field, Icon, Input, NumberInput, Panel, Select, Skeleton, Table, type Column } from '../ui';
import { plural } from '../ui/format';
import { SaveNote } from './SaveNote';

export interface OutfitEconomySectionProps {
  /** The gallery the card loaded: the outfits an item can be. */
  catalog: DesktopMascotCatalog;
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
}

type Read =
  | { status: 'loading' }
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: EconomyView };

const NEW_KEEP = 'new-keep';

/** The kyber crystal, drawn in its rarity's token (ui/kit.css .ui-kyber-*), with the rarity as its name. */
export function KyberCrystalIcon({ rarity, size = 14 }: { rarity: Rarity; size?: number }) {
  return (
    <span className={`ui-kyber ui-kyber-${crystalOf(rarity)}`} role="img" aria-label={`${RARITY_LABELS[rarity]} kyber crystal`} data-rarity={rarity}>
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" focusable="false"><path d={KYBER_CRYSTAL_PATH} /></svg>
    </span>
  );
}

export function OutfitEconomySection({ catalog, locked }: OutfitEconomySectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // The item being edited: an outfit id, a keep item id, or NEW_KEEP for a keep item not yet saved.
  const [itemId, setItemId] = useState('');
  const [keepId, setKeepId] = useState('');
  const [form, setForm] = useState<ItemForm | null>(null);
  const [collectionForm, setCollectionForm] = useState({ id: '', name: '' });
  const [packForm, setPackForm] = useState({ id: '', name: '', collectionId: '', items: [] as string[], supplyKind: 'capped' as 'capped' | 'unlimited', cap: '100', priceHint: '', status: 'active' as ItemStatus });
  const [report, setReport] = useState<SupplyRow[] | null>(null);

  const load = useCallback(async (): Promise<EconomyView | null> => {
    try {
      const view = await api.desktopMascotEconomy();
      setRead(view === null ? { status: 'absent' } : { status: 'loaded', view });
      return view;
    } catch (error) {
      setRead({ status: 'failed', text: economyRefusalText(error, ECONOMY_UNREADABLE_TEXT) });
      return null;
    }
  }, []);
  const loadReport = useCallback(async () => {
    try {
      setReport((await api.desktopMascotEconomySupply()).rows);
    } catch {
      setReport(null);
    }
  }, []);

  useEffect(() => {
    void load().then((view) => { if (view) void loadReport(); });
  }, [load, loadReport]);

  const view = read.status === 'loaded' ? read.view : null;
  // Every outfit Gyro keeps, by id: the gallery's and the retired ones (each a product for the store), by name.
  const outfits = useMemo(() => {
    const rows = [...catalog.outfits.map((outfit) => ({ id: outfit.id, name: outfit.name })), ...(catalog.retired ?? []).map((kept) => ({ id: kept.id, name: `${kept.name} (retired)` }))];
    return rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [catalog]);
  const nameOf = (id: string): string => outfits.find((outfit) => outfit.id === id)?.name ?? id;
  const keepItems = view ? Object.entries(view.items).filter(([, item]) => item.kind === 'keep') : [];
  const editingId = itemId === NEW_KEEP ? keepId.trim() : itemId;
  const current = view && editingId ? view.items[editingId] : undefined;
  const minted = view && editingId ? view.supply[editingId]?.minted ?? 0 : 0;

  const select = (id: string) => {
    setItemId(id);
    setNote(null);
    if (!id) {
      setForm(null);
      return;
    }
    if (id === NEW_KEEP) {
      setKeepId('');
      setForm(itemFormOf(undefined, 'keep', 'rare'));
      return;
    }
    const item = view?.items[id];
    setForm(itemFormOf(item, item?.kind === 'keep' ? 'keep' : 'outfit'));
  };

  const saved = (next: EconomyView, text = ECONOMY_SAVED_TEXT) => {
    setRead({ status: 'loaded', view: next });
    setNote({ ok: true, text: next.auditPending ? `${text} ${next.message ?? ''}`.trim() : text });
    void loadReport();
  };
  const refused = async (error: unknown) => {
    setNote({ ok: false, text: economyRefusalText(error, 'The change could not be saved.') });
    if (economyChanged(error)) await load();
  };

  const saveItem = async () => {
    if (!view || !form) return;
    const id = editingId;
    if (form.kind === 'keep' && !KEEP_ITEM_ID_RE.test(id)) {
      setNote({ ok: false, text: 'A keep item id is "k-" and 10 lower-case letters or digits.' });
      return;
    }
    const refusal = itemFormRefusal(form, current, minted);
    if (refusal) {
      setNote({ ok: false, text: refusal });
      return;
    }
    setBusy(true);
    try {
      const next = await api.saveDesktopMascotEconomyItem(id, itemBody(form, view.revision));
      saved(next);
      if (itemId === NEW_KEEP) setItemId(id);
      setForm(itemFormOf(next.items[id], form.kind));
    } catch (error) {
      await refused(error);
    } finally {
      setBusy(false);
    }
  };

  const saveCollection = async () => {
    if (!view) return;
    const id = collectionForm.id.trim();
    const name = collectionForm.name.trim();
    if (!COLLECTION_ID_RE.test(id)) {
      setNote({ ok: false, text: 'A collection id is 1 to 32 lower-case letters, digits or dashes.' });
      return;
    }
    if (!name || name.length > ECONOMY_NAME_MAX) {
      setNote({ ok: false, text: `A collection has a name of 1 to ${ECONOMY_NAME_MAX} characters.` });
      return;
    }
    setBusy(true);
    try {
      saved(await api.saveDesktopMascotEconomyCollection(id, { expectedRevision: view.revision, name }));
    } catch (error) {
      await refused(error);
    } finally {
      setBusy(false);
    }
  };

  const savePack = async () => {
    if (!view) return;
    const id = packForm.id.trim();
    const name = packForm.name.trim();
    const supply = supplyOf(packForm);
    if (!PACK_ID_RE.test(id)) {
      setNote({ ok: false, text: 'A pack id is "p-" and 10 lower-case letters or digits.' });
      return;
    }
    if (!name || name.length > ECONOMY_NAME_MAX || !packForm.collectionId || packForm.items.length === 0 || packForm.items.length > PACK_ITEMS_MAX || !supply) {
      setNote({ ok: false, text: `A pack has a name, a collection, 1 to ${PACK_ITEMS_MAX} items and a supply.` });
      return;
    }
    const priceHint = packForm.priceHint.trim() ? Number(packForm.priceHint.trim()) : null;
    if (priceHint !== null && (!Number.isSafeInteger(priceHint) || priceHint < 1)) {
      setNote({ ok: false, text: 'A price hint is a whole number of Kyber credits.' });
      return;
    }
    setBusy(true);
    try {
      saved(await api.saveDesktopMascotEconomyPack(id, { expectedRevision: view.revision, name, collectionId: packForm.collectionId, items: packForm.items, supply, priceHint, status: packForm.status }));
    } catch (error) {
      await refused(error);
    } finally {
      setBusy(false);
    }
  };

  if (read.status === 'absent') return null;
  const disabled = locked || busy || !view;
  const reportColumns: Column<SupplyRow>[] = [
    { key: 'item', header: 'Outfit', cell: (row) => <span className="ui-kyber-row"><KyberCrystalIcon rarity={row.rarity} />{nameOf(row.itemId)}</span> },
    { key: 'rarity', header: 'Rarity', cell: (row) => RARITY_LABELS[row.rarity], muted: true },
    { key: 'cap', header: 'Copies', cell: (row) => row.cap.toLocaleString('en-US'), numeric: true },
    { key: 'minted', header: 'Given out', cell: (row) => row.minted.toLocaleString('en-US'), numeric: true },
    { key: 'remaining', header: 'Left', cell: (row) => row.remaining.toLocaleString('en-US'), numeric: true },
    { key: 'holders', header: 'Holders', cell: (row) => row.holders.toLocaleString('en-US'), numeric: true },
    { key: 'status', header: 'Status', cell: (row) => (row.soldOut ? `Sold out${row.soldOutAtMs ? ` ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(row.soldOutAtMs)}` : ''}` : row.status === 'active' ? 'On sale' : row.status), muted: true, hideOnPhone: true },
  ];
  return (
    <Panel
      heading="h3"
      titleId="mascot-economy-title"
      title={<span className="ui-title-icon"><Icon icon={Gem} size="md" />Economy</span>}
      description="Rarity, copies, collections and packs: what makes an outfit collectible"
      actions={(
        <Button size="sm" variant="ghost" icon={RefreshCw} disabled={busy} onClick={() => { void load(); void loadReport(); }} title="Read the economy again">
          Refresh
        </Button>
      )}
      footer={<span>{ECONOMY_HINT}</span>}
    >
      <div className="ui-form">
        {read.status === 'loading' && <Skeleton size="md" width="60%" />}
        {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
        {view && (
          <>
            <Field label="Item" help="An outfit Gyro keeps, a keep item (the buy-to-keep twin of a seasonal or day outfit), or a new keep item.">
              <Select value={itemId} disabled={locked || busy} onChange={(event) => select(event.target.value)} data-testid="economy-item">
                <option value="">Choose an item…</option>
                {outfits.map((outfit) => {
                  const item = view.items[outfit.id];
                  return <option key={outfit.id} value={outfit.id}>{outfit.name}{item ? ` · ${RARITY_LABELS[item.rarity]} · ${supplyText(item.supply)}` : ''}</option>;
                })}
                {keepItems.map(([id, item]) => <option key={id} value={id}>{id} (keeps {nameOf(item.of ?? '')}) · {RARITY_LABELS[item.rarity]}</option>)}
                <option value={NEW_KEEP}>New keep item…</option>
              </Select>
            </Field>
            {form && (
              <div className="ui-form" data-testid="economy-item-form">
                {itemId === NEW_KEEP && (
                  <Field label="Keep item id" help={'"k-" and 10 lower-case letters or digits.'}>
                    <Input value={keepId} disabled={disabled} onChange={(event) => setKeepId(event.target.value)} placeholder="k-0123456789" />
                  </Field>
                )}
                {form.kind === 'keep' && (
                  <Field label="Keeps" help="The seasonal or day outfit this item keeps all year.">
                    <Select value={form.of} disabled={disabled} onChange={(event) => setForm({ ...form, of: event.target.value })}>
                      <option value="">Choose an outfit…</option>
                      {outfits.map((outfit) => <option key={outfit.id} value={outfit.id}>{outfit.name}</option>)}
                    </Select>
                  </Field>
                )}
                <Field label="Rarity" help={bandHintText(form.rarity)}>
                  <span className="ui-kyber-row">
                    <KyberCrystalIcon rarity={form.rarity} size={18} />
                    <Select value={form.rarity} disabled={disabled} onChange={(event) => setForm(withRarity(form, event.target.value as Rarity, !current || minted === 0))} data-testid="economy-rarity">
                      {RARITIES.map((rarity) => <option key={rarity} value={rarity}>{rarityOptionText(rarity)}</option>)}
                    </Select>
                  </span>
                </Field>
                <Field label="Copies" help={supplyStatusText(view.supply[editingId], supplyOf(form) ?? { kind: 'unlimited' })}>
                  <span className="ui-kyber-row">
                    <Select value={form.supplyKind} disabled={disabled} onChange={(event) => setForm({ ...form, supplyKind: event.target.value as 'unlimited' | 'capped' })} data-testid="economy-supply">
                      <option value="unlimited">Unlimited</option>
                      <option value="capped">A fixed number</option>
                    </Select>
                    {form.supplyKind === 'capped' && <NumberInput value={form.cap} disabled={disabled} inputMode="numeric" aria-label="Cap" suffix="copies" onChange={(event) => setForm({ ...form, cap: event.target.value })} data-testid="economy-cap" />}
                  </span>
                </Field>
                <Field label="Status" help="Retired: no new copies, owners keep theirs. Withdrawn: nobody wears it, and it stays withdrawn.">
                  <Select value={form.status} disabled={disabled} onChange={(event) => setForm({ ...form, status: event.target.value as ItemStatus })} data-testid="economy-status">
                    {ITEM_STATUSES.map((status) => <option key={status} value={status}>{status[0]!.toUpperCase()}{status.slice(1)}</option>)}
                  </Select>
                </Field>
                <Checkbox label="Can be traded or sold between people" checked={form.tradeable} disabled={disabled} onChange={(event) => setForm({ ...form, tradeable: event.target.checked })} />
                <Field label="Collection">
                  <Select value={form.collectionId} disabled={disabled} onChange={(event) => setForm({ ...form, collectionId: event.target.value })}>
                    <option value="">None</option>
                    {Object.values(view.collections).map((collection) => <option key={collection.id} value={collection.id}>{collection.name}</option>)}
                  </Select>
                </Field>
                <Field label="Animations needed" help={animationsGapText(form.rarity, view.animations ? view.animations[editingId] ?? 0 : null) ?? 'Mascot animations are off on this service, so the packs bound cannot be counted.'}>
                  <Select value={String(form.animations)} disabled={disabled} onChange={(event) => setForm({ ...form, animations: Number(event.target.value) as 0 | 1 | 2 | 3 })}>
                    {[0, 1, 2, 3].map((minimum) => <option key={minimum} value={minimum}>{minimum === 0 ? 'None' : plural(minimum, 'action pack')}{minimum === ANIMATIONS_MINIMUM[form.rarity] ? ' (the rarity’s minimum)' : ''}</option>)}
                  </Select>
                </Field>
                <Field label="Greeting id" help="Legendary: the unique greeting (a day greeting id). Leave empty for none.">
                  <Input value={form.greetingId} disabled={disabled} onChange={(event) => setForm({ ...form, greetingId: event.target.value })} />
                </Field>
                <div className="ui-actions">
                  <Button size="sm" disabled={disabled} busy={busy} onClick={() => void saveItem()} data-testid="economy-save-item">Save item</Button>
                </div>
              </div>
            )}

            <Field label="Collections" help={`${plural(Object.keys(view.collections).length, 'collection')}. A collection groups items (Harry Potter, NFL, Halloween 2026); a category says when they show.`}>
              <ul className="ui-list" data-testid="economy-collections">
                {Object.values(view.collections).map((collection) => (
                  <li key={collection.id}><Button size="sm" variant="ghost" disabled={disabled} onClick={() => setCollectionForm({ id: collection.id, name: collection.name })}>{collection.name}</Button> · {collection.id} · {plural(Object.values(view.items).filter((item) => item.collectionId === collection.id).length, 'item')}</li>
                ))}
              </ul>
            </Field>
            <div className="ui-form">
              <Field label="Collection id" help="1 to 32 lower-case letters, digits or dashes; a known id edits that collection.">
                <Input value={collectionForm.id} disabled={disabled} onChange={(event) => setCollectionForm({ ...collectionForm, id: event.target.value })} placeholder="halloween-2026" data-testid="economy-collection-id" />
              </Field>
              <Field label="Collection name">
                <Input value={collectionForm.name} disabled={disabled} maxLength={ECONOMY_NAME_MAX} onChange={(event) => setCollectionForm({ ...collectionForm, name: event.target.value })} data-testid="economy-collection-name" />
              </Field>
              <div className="ui-actions">
                <Button size="sm" variant="secondary" disabled={disabled} busy={busy} onClick={() => void saveCollection()} data-testid="economy-save-collection">Save collection</Button>
              </div>
            </div>

            <Field label="Packs" help={`${plural(Object.keys(view.packs).length, 'pack')}. A pack is a bundle with its own copies and price; buying it gives one copy of each item in it, and it is sold out when any of them is.`}>
              <ul className="ui-list" data-testid="economy-packs">
                {Object.values(view.packs).map((pack) => (
                  <li key={pack.id}>
                    <Button size="sm" variant="ghost" disabled={disabled} onClick={() => setPackForm({ id: pack.id, name: pack.name, collectionId: pack.collectionId, items: [...pack.items], supplyKind: pack.supply.kind, cap: pack.supply.kind === 'capped' ? String(pack.supply.cap) : '', priceHint: pack.priceHint === null ? '' : String(pack.priceHint), status: pack.status })}>{pack.name}</Button>
                    {' · '}{pack.id} · {plural(pack.items.length, 'item')} · {supplyText(pack.supply)} · {pack.status}
                  </li>
                ))}
              </ul>
            </Field>
            <div className="ui-form">
              <Field label="Pack id" help={'"p-" and 10 lower-case letters or digits; a known id edits that pack.'}>
                <Input value={packForm.id} disabled={disabled} onChange={(event) => setPackForm({ ...packForm, id: event.target.value })} placeholder="p-0123456789" data-testid="economy-pack-id" />
              </Field>
              <Field label="Pack name">
                <Input value={packForm.name} disabled={disabled} maxLength={ECONOMY_NAME_MAX} onChange={(event) => setPackForm({ ...packForm, name: event.target.value })} data-testid="economy-pack-name" />
              </Field>
              <Field label="Pack collection">
                <Select value={packForm.collectionId} disabled={disabled} onChange={(event) => setPackForm({ ...packForm, collectionId: event.target.value })}>
                  <option value="">Choose a collection…</option>
                  {Object.values(view.collections).map((collection) => <option key={collection.id} value={collection.id}>{collection.name}</option>)}
                </Select>
              </Field>
              <Field label="Pack items" help="Items with a saved rarity; hold Ctrl to choose several.">
                <Select multiple value={packForm.items} disabled={disabled} onChange={(event) => setPackForm({ ...packForm, items: Array.from(event.target.selectedOptions, (option) => option.value) })} data-testid="economy-pack-items">
                  {Object.keys(view.items).map((id) => <option key={id} value={id}>{nameOf(id)} ({id})</option>)}
                </Select>
              </Field>
              <Field label="Pack copies">
                <span className="ui-kyber-row">
                  <Select value={packForm.supplyKind} disabled={disabled} onChange={(event) => setPackForm({ ...packForm, supplyKind: event.target.value as 'unlimited' | 'capped' })}>
                    <option value="unlimited">Unlimited</option>
                    <option value="capped">A fixed number</option>
                  </Select>
                  {packForm.supplyKind === 'capped' && <NumberInput value={packForm.cap} disabled={disabled} inputMode="numeric" aria-label="Pack cap" suffix="copies" onChange={(event) => setPackForm({ ...packForm, cap: event.target.value })} />}
                </span>
              </Field>
              <Field label="Pack price hint" help="Kyber credits the store should ask; empty proposes the items' bands added up.">
                <NumberInput value={packForm.priceHint} disabled={disabled} inputMode="numeric" suffix="credits" onChange={(event) => setPackForm({ ...packForm, priceHint: event.target.value })} />
              </Field>
              <Field label="Pack status">
                <Select value={packForm.status} disabled={disabled} onChange={(event) => setPackForm({ ...packForm, status: event.target.value as ItemStatus })}>
                  {ITEM_STATUSES.map((status) => <option key={status} value={status}>{status[0]!.toUpperCase()}{status.slice(1)}</option>)}
                </Select>
              </Field>
              <div className="ui-actions">
                <Button size="sm" variant="secondary" disabled={disabled} busy={busy} onClick={() => void savePack()} data-testid="economy-save-pack">Save pack</Button>
              </div>
            </div>

            <SaveNote note={note} />

            <Field label="Supply report" help="Every outfit with a fixed number of copies: given out, left, sold-out date and how many people hold one.">
              {report === null
                ? <p className="ui-cell-stack">The supply report could not be read just now.</p>
                : report.length === 0
                  ? <p className="ui-cell-stack">No outfit has a fixed number of copies yet.</p>
                  : <Table label="Supply report" columns={reportColumns} rows={report} rowKey={(row) => row.itemId} />}
            </Field>

            {view.history.length > 0 && (
              <details className="ui-details">
                <summary>History ({view.history.length})</summary>
                <ul className="ui-list ui-history">
                  {view.history.slice(0, 20).map((line) => <li key={`${line.atMs}-${line.id}`}>{historyLineText(line)}</li>)}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
